/**
 * Status — Real public health status (no auth, no mocked data)
 *
 * Wired to /api/status/components (real HTTP probes against Mongo + AI
 * providers + Resend + Frankfurter) and /api/status/uptime (90-day
 * uptime computed from real probe snapshots).
 *
 * This page replaces the previous statically-mocked version (v34.x) which
 * showed fake "99.97%" uptime numbers. Per the trust-first principle:
 * we either show real data or we don't show the number.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  operational: { color: 'emerald', icon: CheckCircle2, label: 'Operational' },
  degraded: { color: 'amber', icon: AlertTriangle, label: 'Degraded' },
  down: { color: 'rose', icon: XCircle, label: 'Down' },
  unknown: { color: 'slate', icon: AlertTriangle, label: 'Unknown' },
};

const OVERALL_META = {
  operational: { color: 'emerald', label: 'All systems operational' },
  degraded_performance: { color: 'amber', label: 'Degraded performance' },
  partial_outage: { color: 'amber', label: 'Partial outage' },
  major_outage: { color: 'rose', label: 'Major outage' },
};

export default function Status() {
  const [snapshot, setSnapshot] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [c, u] = await Promise.all([
        axios.get(`${API}/status/components`).catch(() => ({ data: null })),
        axios.get(`${API}/status/uptime`).catch(() => ({ data: null })),
      ]);
      setSnapshot(c.data);
      setUptime(u.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const overall = snapshot?.overall || 'unknown';
  const overallMeta = OVERALL_META[overall] || OVERALL_META.operational;
  const overallColor = {
    emerald: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    amber:   'bg-amber-500/10   border-amber-500/40   text-amber-300',
    rose:    'bg-rose-500/10    border-rose-500/40    text-rose-300',
  }[overallMeta.color];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>System Status · CARTIE</title>
        <meta name="description" content="Real-time component health for CARTIE. Live HTTP probes against MongoDB, AI providers, and external dependencies." />
      </Helmet>

      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="status-back">
          ← CARTIE
        </Link>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="text-xs font-semibold text-slate-400 hover:text-emerald-300 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="status-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </header>

      <section className="px-4 py-12 max-w-4xl mx-auto" data-testid="status-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-4">
          <Activity className="w-3 h-3" /> Live · Auto-refresh 30s
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="status-h1">System Status</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
          Live HTTP probes against every external dependency we use. No mocked data.
          If a probe fails here, it really failed in our pod.
        </p>

        <div className={`mt-6 rounded-2xl border ${overallColor} px-5 py-4 flex items-center justify-between`} data-testid="status-overall">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Overall</div>
            <div className="text-xl font-bold mt-0.5" data-testid="status-overall-label">{loading ? 'Checking…' : overallMeta.label}</div>
          </div>
          <div className="text-right text-[11px] font-mono opacity-80">
            {snapshot?.checked_at && (
              <>checked: {new Date(snapshot.checked_at).toLocaleString()}<br /></>
            )}
            next: ~{snapshot?.next_check_recommended_in_seconds || 30}s
          </div>
        </div>
      </section>

      <section className="px-4 pb-12 max-w-4xl mx-auto">
        <h2 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Components</h2>
        <div className="space-y-2" data-testid="status-components-list">
          {(snapshot?.components || []).map((c, i) => (
            <ComponentRow key={i} c={c} testid={`status-component-${i}`} />
          ))}
          {!loading && (snapshot?.components || []).length === 0 && (
            <div className="text-sm text-slate-500 text-center p-6">No component data available.</div>
          )}
        </div>
      </section>

      {uptime?.days && uptime.days.length > 0 && (
        <section className="px-4 pb-12 max-w-4xl mx-auto" data-testid="status-uptime-section">
          <h2 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">90-day uptime · real probe data</h2>
          <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
            <div className="flex items-end gap-[2px] h-12">
              {uptime.days.map((d, i) => {
                const pct = Number(d.uptime_pct || 100);
                const color = pct >= 99.9 ? 'bg-emerald-500' : pct >= 99 ? 'bg-amber-500' : 'bg-rose-500';
                return (
                  <div
                    key={i}
                    title={`${d.date}: ${pct.toFixed(2)}%`}
                    className={`flex-1 ${color} rounded-sm opacity-90 hover:opacity-100 transition-opacity`}
                    style={{ height: `${Math.max(8, Math.min(100, pct))}%` }}
                  />
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500 font-mono">
              <span>90 days ago</span>
              <span className="text-emerald-300" data-testid="status-uptime-average">avg uptime: {Number(uptime.average_uptime_pct ?? 100).toFixed(3)}%</span>
              <span>today</span>
            </div>
          </div>
        </section>
      )}

      <footer className="px-4 py-6 text-center text-xs text-slate-500 border-t border-white/5">
        Endpoints: <code className="text-emerald-400">/api/status/components</code> ·
        {' '}<code className="text-emerald-400">/api/status/uptime</code> ·
        {' '}<Link to="/security" className="text-emerald-400 hover:underline">Security</Link> ·
        {' '}<Link to="/roadmap" className="text-emerald-400 hover:underline">Roadmap</Link>
      </footer>
    </div>
  );
}

function ComponentRow({ c, testid }) {
  const meta = STATUS_META[c.status] || STATUS_META.unknown;
  const Icon = meta.icon;
  const colorClass = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
    slate: 'text-slate-400',
  }[meta.color];
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/40 px-4 py-3 flex items-center justify-between" data-testid={testid}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${colorClass}`} />
        <div>
          <div className="text-sm font-bold text-white" data-testid={`${testid}-name`}>{c.name}</div>
          {c.detail && <div className="text-[10px] text-slate-500 font-mono">{c.detail}</div>}
        </div>
      </div>
      <div className="text-right">
        <div className={`text-xs font-bold uppercase tracking-wider ${colorClass}`} data-testid={`${testid}-status`}>{meta.label}</div>
        {c.latency_ms != null && (
          <div className="text-[10px] text-slate-500 font-mono">{c.latency_ms}ms</div>
        )}
      </div>
    </div>
  );
}
